import {
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Divider, SimpleGrid } from '@chakra-ui/layout'
import { Flex, Stack, useToast } from '@chakra-ui/react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { isEmpty } from 'lodash'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { cpf } from 'cpf-cnpj-validator'
import { useTranslation } from 'react-i18next'

import { AccreditedForm } from '../../../../../../@types/forms/forms.types'
import { Input } from '../../../../../../components/Input'
import { NumberInput } from '../../../../../../components/Input/Number'
import { accreditedSchema } from '../../../../../../schemas/form.schemas'
import { Select } from '../../../../../../components/Select'
import { SelectProps } from '../../../../../../@types/forms/forms.types'
import { useAuth } from '../../../../../../hooks/useAuth'
import { Monthly } from '../../../../../../@types/entities/entities.types'
import { axios } from '../../../../../../services/axios'
import { VehicleTypeEnum } from '../../../../../../schemas/enum.schemas'
import { formatErrorMessage } from '../../../../../../helpers/errors'
import { validateBr } from 'js-brasil'
import { queryClient } from '../../../../../../services/react-query'

type ModalProps = {
  isOpen: boolean
  onClose: () => void
}

export function Modal({ isOpen, onClose }: ModalProps) {
  const toast = useToast()
  const { t } = useTranslation()
  const { user } = useAuth()

  const {
    register,
    handleSubmit,
    control,
    setFocus,
    setError,
    formState: { isSubmitting, errors },
    reset,
  } = useForm<AccreditedForm>({
    resolver: zodResolver(accreditedSchema),
  })

  const list_type_car = Object.keys(VehicleTypeEnum.enum).map(type_car => {
    return {
      label: t(type_car),
      value: type_car,
    }
  })

  const { data: list_monthly } = useQuery(
    ['parking-monthly'],
    async () => {
      const response = await axios().get<Monthly[]>(
        `monthly/${user?.parking.id}/parking`
      )

      return response.data.map(monthly => {
        return {
          label: monthly.name,
          value: monthly.id,
        }
      })
    },
    {
      enabled: !isEmpty(user),
    }
  )

  const handleCreateAcrredited: SubmitHandler<AccreditedForm> = async (
    values,
    event
  ) => {
    try {
      event?.preventDefault()

      if (!cpf.isValid(values.cpf)) {
        return toast({
          id: new Date().getTime(),
          title: 'CPF inválido',
          description: 'Por favor, digite um CPF valido!',
          status: 'error',
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
        })
      }

      if (!validateBr.placa(values.vehicle_license_plate)) {
        setFocus('vehicle_license_plate')
        setError('vehicle_license_plate', {
          message: 'Placa inválida',
        })
        return
      }

      const data = {
        ...values,
        parking_id: user?.parking.id,
        phone: values.phone.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s/g, ''),
        vehicle_type: values.vehicle_type.value,
        monthly_id: values.monthly_id?.value || null,
      }

      await axios().post('/accredited', data)

      reset()
      onClose()
      await queryClient.invalidateQueries(['accredited'])
      toast({
        title: 'Credenciado criado com sucesso!',
        status: 'success',
        position: 'top-right',
        variant: 'left-accent',
        isClosable: true,
      })
    } catch (err) {
      const message = formatErrorMessage(err as Error)

      !toast.isActive(message) &&
        toast({
          id: message,
          title: t('toast_error_title'),
          description: t(message),
          status: 'error',
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
        })
    }
  }

  return (
    <ChakraModal isCentered isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay backdropFilter="blur(10px) hue-rotate(90deg)" />
      <ModalContent marginTop={{ base: 'auto', tablet: 'auto' }} mb="auto">
        <ModalHeader>Adicionar credenciado</ModalHeader>
        <ModalCloseButton marginTop="0.5rem" />
        <Divider height="1rem" />
        <ModalBody mb="2rem">
          <SimpleGrid
            as="form"
            onSubmit={handleSubmit(handleCreateAcrredited)}
            columns={{ base: 1, tablet: 1 }}
            spacingX="1rem"
            spacingY="1rem"
          >
            <Input
              id="name"
              type="text"
              label="Nome do credenciado"
              placeholder="Nome"
              error={errors.name}
              {...register('name')}
            />
            <Stack direction="row" spacing="1rem">
              <Select<AccreditedForm, SelectProps, false>
                name="monthly_id"
                control={control}
                size="lg"
                label="Vinculado a"
                options={list_monthly}
                placeholder="Selecione um mensalista"
                selectedOptionStyle="check"
                closeMenuOnSelect={false}
              />
              <NumberInput
                id="cpf"
                type="text"
                label="CPF"
                placeholder="111.111.111-11"
                mask="_"
                format="###.###.###-##"
                error={errors.cpf}
                {...register('cpf')}
              />
            </Stack>
            <Stack direction="row" spacing="1rem">
              <Input
                id="email"
                type="text"
                label="E-mail"
                placeholder="parkoff@exemplo.com"
                error={errors.email}
                {...register('email')}
              />
              <NumberInput
                id="phone"
                type="tel"
                label="Celular"
                placeholder="DDD + Número"
                format="## #####-####"
                mask="_"
                error={errors.phone}
                {...register('phone', {
                  setValueAs: value => value.replace(/[^a-zA-Z0-9 ]/g, ''),
                })}
              />
            </Stack>
            <Stack direction="row" spacing="1rem">
              <Select<AccreditedForm, SelectProps, false>
                name="vehicle_type"
                control={control}
                size="lg"
                label="Tipo de veículo"
                options={list_type_car}
                placeholder="Selecione um tipo"
                selectedOptionStyle="check"
                closeMenuOnSelect={false}
              />
              <Input
                id="vehicle_license_plate"
                type="text"
                label="Placa"
                maxLength={7}
                placeholder="DH7W82V"
                error={errors.vehicle_license_plate}
                {...register('vehicle_license_plate', {
                  setValueAs: value => value.toUpperCase(),
                })}
              />
            </Stack>
            <Stack direction="row" spacing="1rem">
              <Input
                id="vehicle_model"
                type="text"
                label="Modelo"
                placeholder="Creta"
                error={errors.vehicle_model}
                {...register('vehicle_model')}
              />
              <Input
                id="vehicle_collor"
                type="text"
                label="Cor"
                placeholder="Preto"
                error={errors.vehicle_collor}
                {...register('vehicle_collor')}
              />
            </Stack>
            <Stack direction="row" justifySelf="flex-end" mt="2rem">
              <Flex
                fontSize="sm"
                width="12rem"
                height="3rem"
                alignItems="center"
                justifyContent="center"
                background="parkoff.orange.300"
                textColor="white"
                borderRadius="md"
                as="button"
                type="submit"
                disabled={isSubmitting}
              >
                Adicionar credenciado
              </Flex>
            </Stack>
          </SimpleGrid>
        </ModalBody>
      </ModalContent>
    </ChakraModal>
  )
}
